import { ZfIcon, ZfIconButton, ZfSlider } from '@zafire/react';
import type { Dispatch, FunctionComponent, SetStateAction } from 'react';
import { useRef } from 'react';
import { useShadowRootCustomStyles } from '../../../app/hooks/useShadowRootCustomStyles';
import { LIQUIDITY_NEED_DETAILS_MIN_VISIBLE_COLUMNS } from '../../config/constants';

interface ILiquidityNeedDetailsZoomSliderProps {
    totalColumns: number;
    visibleColumns: number;
    setVisibleColumns: Dispatch<SetStateAction<number>>;
}

export const LiquidityNeedDetailsZoomSlider: FunctionComponent<ILiquidityNeedDetailsZoomSliderProps> = ({
    totalColumns,
    visibleColumns,
    setVisibleColumns,
}) => {
    const sliderRef = useRef<HTMLZfSliderElement>(null);
    useShadowRootCustomStyles(sliderRef, '.slider__main--track { width: 150px } .tooltip { visibility: hidden !important; }');

    const minVisible = LIQUIDITY_NEED_DETAILS_MIN_VISIBLE_COLUMNS;
    const maxVisible = totalColumns;

    // The slider runs inverted, so dragging right zooms in (fewer columns). This mirrors a column count to a slider position and back.
    const mirror = (columns: number) => minVisible + maxVisible - columns;

    const zoomIn = () => setVisibleColumns((visible) => Math.max(minVisible, visible - 1));
    const zoomOut = () => setVisibleColumns((visible) => Math.min(maxVisible, visible + 1));

    return (
        <ZfSlider
            ref={sliderRef}
            slot="content"
            style={{ display: 'flex', justifyContent: 'center' }}
            value={mirror(visibleColumns)}
            min={minVisible}
            max={maxVisible}
            step={1}
            size="medium"
            onValueChangeCommitted={(event: CustomEvent<number>) => setVisibleColumns(mirror(event.detail))}
        >
            <ZfIconButton slot="start" hierarchy="tertiary" size="small" disabled={visibleColumns >= maxVisible} onClick={zoomOut}>
                <ZfIcon name="remove" size="medium" aria-hidden="true" />
            </ZfIconButton>
            <ZfIconButton slot="end" hierarchy="tertiary" size="small" disabled={visibleColumns <= minVisible} onClick={zoomIn}>
                <ZfIcon name="add" size="medium" aria-hidden="true" />
            </ZfIconButton>
        </ZfSlider>
    );
};
